import React, { useEffect, useRef, useState } from 'react'
import { BodyRow, HeadRow, StyledCardContainer, Table } from '../styles/styles'
import { canManageAssetSetup } from '../../permission/Permissions'
import { Button } from 'react-bootstrap'
import globalNotification from 'live/services/global-notification'
import { OrderingHeader } from 'live/components/ui/List'
import dateService from 'live/services/date'
import { CSVLink } from 'react-csv'

interface Props {
    selected: {
        asset: {
            id: string
            name: string
        }
    }
}

interface WITSMLObject {
    name: string
    objectType?: string
    uid?: string
    timestamp?: number
    length: number
}

export const UpdateAsset: React.FC<Props> = ({ selected }: Props) => {
    const [objects, setObjects] = useState<WITSMLObject[]>([])
    const [objectData, setObjectData] = useState<[]>([])
    const [objectUID, setObjectUID] = useState<string>('')
    const [collapsed, setCollapsed] = useState(true)

    function toKiloBytes(bytes: number): string {
        const kilobytes = bytes / 1024

        return `${kilobytes.toFixed(2)} KB`
    }

    useEffect(() => {
        const url = `/services/plugin-asset-setup/asset/objects?wellboreId=${selected.asset.id}`

        fetch(url)
            .then(result => {
                if (result.ok) {
                    return result.json()
                } else {
                    throw new Error()
                }
            })
            .then(json => setObjects(json))
            .catch(e => globalNotification.error(`An error occurred on fetching witsml objects data: ${e.message}`))
    }, [selected.asset.id])

    const handleUpdate = (wellboreId: string): void => {
        fetch(`/services/plugin-asset-setup/asset/updateAsset?wellboreId=${wellboreId}`, {
            method: 'GET'
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok')
                }
            })
            .then(() => globalNotification.success(`Update for wellbore ${selected.asset.name} requested`))
            .catch(e => globalNotification.error(`An error occurred when requesting wellbore update : ${e.message}`))
    }

    const handleGetCollectionData = (wellboreId: string, objectType: string, uid: string): void => {
        const url = `/services/plugin-asset-setup/asset/getObjectData?wellboreId=${wellboreId}&objectType=${objectType}&uid=${uid}`

        fetch(url)
            .then(result => {
                if (result.ok) {
                    return result.json()
                } else {
                    throw new Error()
                }
            })

            .then(json => setObjectData(json))
            .then(() => setObjectUID(uid))
            .catch(e => globalNotification.error(`An error occurred on fetching ${objectType} data: ${e.message}`))
    }

    const handleDownloadJson = (uid: string): void => {
        const dataStr = JSON.stringify(objectData)
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)
        const downloadNode = downloadLink.current
        downloadNode.setAttribute('href', dataUri)
        downloadNode.setAttribute('download', `${uid}.json`)
        downloadNode.click()
    }

    const downloadLink = useRef()

    return (
        <StyledCardContainer
            title={`Asset Setup Data`}
            subtitle={` - Updated on ${dateService.fromNow(
                objects.reduce(
                    (oldest, item) => (item.timestamp > oldest ? item?.timestamp : oldest),
                    objects[0]?.timestamp
                )
            )}`}
            collapsed={collapsed}
            renderControls={() => {
                return (
                    <>
                        {canManageAssetSetup() && (
                            <Button onClick={() => handleUpdate(selected.asset.id)}>
                                <i className="far fa-sync-alt" />
                            </Button>
                        )}
                        <Button onClick={() => setCollapsed(!collapsed)}>
                            <i className={collapsed ? 'far fa-angle-up' : 'far fa-angle-down'} />
                        </Button>
                    </>
                )
            }}
        >
            <Table>
                <HeadRow>
                    <OrderingHeader>Object Name</OrderingHeader>
                    <OrderingHeader>Size on Disk</OrderingHeader>
                    <OrderingHeader>Updated On</OrderingHeader>
                    <OrderingHeader />
                    <OrderingHeader>Action</OrderingHeader>
                    <OrderingHeader />
                    <OrderingHeader />
                </HeadRow>
                {objects.map(item => (
                    <BodyRow key={`${item.uid}`}>
                        <div>
                            <strong>
                                {item.objectType} ({item.uid})
                            </strong>
                        </div>
                        <div>{toKiloBytes(item.length)}</div>
                        <div>{item.timestamp && dateService.formatDateTime(item.timestamp)}</div>
                        <div>{item.timestamp && dateService.fromNow(item.timestamp)}</div>
                        <div>
                            {canManageAssetSetup() && (
                                <Button
                                    id={`get-${item.name}`}
                                    variant={'default'}
                                    onClick={() =>
                                        handleGetCollectionData(selected.asset.id, item.objectType, item.uid)
                                    }
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    Get Data
                                </Button>
                            )}
                        </div>
                        <div>
                            {objectData.length > 0 && objectUID === item.uid && (
                                <CSVLink
                                    id={`csv-link-${item.uid}`}
                                    data={objectData}
                                    filename={`${item.uid}.csv`}
                                    className={'btn btn-default'}
                                >
                                    CSV
                                    <i className={'far fa-download'} style={{ marginLeft: '10px' }} />
                                </CSVLink>
                            )}
                        </div>
                        <div>
                            {objectData.length > 0 && objectUID === item.uid && (
                                <>
                                    <Button id={`json-${item.uid}`} onClick={() => handleDownloadJson(item.uid)}>
                                        JSON
                                        <i className={'far fa-download'} style={{ marginLeft: '10px' }} />
                                    </Button>
                                    <a ref={downloadLink} style={{ display: 'none' }} />
                                </>
                            )}
                        </div>
                    </BodyRow>
                ))}
            </Table>
        </StyledCardContainer>
    )
}
